import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePolicyLines } from "@/hooks/usePolicyLines";

interface PolicyTypeDialogFormProps {
  onClose: () => void;
  onPolicyLineAdded?: () => void;
}

export const PolicyTypeDialogForm = ({ onClose, onPolicyLineAdded }: PolicyTypeDialogFormProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [policyLineId, setPolicyLineId] = useState("");
  const { toast } = useToast();
  const { data: policyLines } = usePolicyLines();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { error } = await supabase
        .from('policy_types')
        .insert([{ 
          name,
          description,
          policy_line_id: policyLineId || null
        }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Policy type added successfully",
      });

      onClose();
      setName("");
      setDescription("");
      setPolicyLineId("");
      
      if (onPolicyLineAdded) {
        onPolicyLineAdded();
      }
    } catch (error) {
      console.error('Error adding policy type:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add policy type",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="policyLine">Policy Line</Label>
        <Select value={policyLineId} onValueChange={setPolicyLineId}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a policy line" />
          </SelectTrigger>
          <SelectContent>
            {policyLines?.map((line) => (
              <SelectItem key={line.id} value={line.id}>
                {line.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter policy type name"
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter description"
        />
      </div>
      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit">Add</Button>
      </div>
    </form>
  );
};